import { useSearchParams } from 'react-router-dom';
import Segmented from '../../../components/ui/Segmented';
import ExamsView from './ExamsView';
import ProgressView from './ProgressView';
import GapsView from './GapsView';
import PracticeView from './PracticeView';
import NationalView from './NationalView';

export type AcademicsView = 'exams' | 'progress' | 'gaps' | 'practice' | 'national';

const VIEWS: { key: AcademicsView; label: string; help: string }[] = [
  { key: 'exams', label: 'Exams', help: 'Every exam attempt; open one to see it question by question against the class.' },
  { key: 'progress', label: 'Progress', help: 'Weekly average against the class, score spread, rank movement and trends.' },
  { key: 'gaps', label: 'Strengths & gaps', help: 'Mastery by subject and chapter, chapters missed again and again, accuracy by difficulty and where blanks fall.' },
  { key: 'practice', label: 'Practice & tasks', help: 'Practice against real exams, and how reliably worksheets, homework and exams are done.' },
  { key: 'national', label: 'National exam', help: 'Readiness for the national exam on the chapters it is expected to cover.' },
];
const KEYS = VIEWS.map((v) => v.key);

/** Student 360 › Academics: one sub-tab per question a teacher asks about the student's learning. */
export default function AcademicsTab({ id, studentName, cohort }: {
  id: number; studentName: string; cohort?: { gradeId: number | null; sectionId: number | null };
}) {
  const [sp, setSp] = useSearchParams();
  const raw = sp.get('view');
  const view: AcademicsView = KEYS.includes(raw as AcademicsView) ? (raw as AcademicsView) : 'exams';
  const examParam = Number(sp.get('exam'));
  const openResult = view === 'exams' && Number.isFinite(examParam) && examParam > 0 ? examParam : null;
  const update = (patch: Record<string, string | null>) => {
    const next = new URLSearchParams(sp);
    Object.entries(patch).forEach(([k, v]) => (v == null ? next.delete(k) : next.set(k, v)));
    setSp(next, { replace: true });
  };

  return (
    <div className="space-y-4">
      <Segmented<AcademicsView> dense tabs scroll label="Academics view"
        options={VIEWS} value={view} onChange={(v) => update({ view: v === 'exams' ? null : v, exam: null })} />
      {view === 'exams' && <ExamsView id={id} studentName={studentName} openResult={openResult}
        onOpenResult={(r) => update({ exam: r == null ? null : String(r) })} />}
      {view === 'progress' && <ProgressView id={id} studentName={studentName} cohort={cohort} />}
      {view === 'gaps' && <GapsView id={id} studentName={studentName} cohort={cohort} />}
      {view === 'practice' && <PracticeView id={id} studentName={studentName} />}
      {view === 'national' && <NationalView id={id} studentName={studentName} />}
    </div>
  );
}
